import React, { Component } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import CardLessDataTable from "../datatables/CardLessDataTableWithSearch";
import { THEME, INIT_CATEGORY_FORM, INIT_SUBCATEGORY_FORM } from "./lectureLibraryConstants";
import { validateCategoryForm, validateSubCategoryForm } from "./lectureLibraryValidation";
import {
  CREATE_LECTURE_CATEGORY,
  UPDATE_LECTURE_CATEGORY,
  DELETE_LECTURE_CATEGORY,
  CREATE_LECTURE_SUBCATEGORY,
  UPDATE_LECTURE_SUBCATEGORY,
  DELETE_LECTURE_SUBCATEGORY
} from "./lectureLibraryService";

// Helper Status Badge component
const StatusBadge = ({ status }) => {
  const active = status === 1 || status === "1" || status === true;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: "20px",
        fontSize: "11px",
        fontWeight: "700",
        background: active ? "#dcfce7" : "#fee2e2",
        color: active ? "#059669" : "#dc2626",
      }}
    >
      {active ? "Active" : "Inactive"}
    </span>
  );
};

class LectureMasters extends Component {
  constructor(props) {
    super(props);
    this.state = {
      subTab: "categories", // 'categories' | 'subcategories'

      // Category Form Modal State
      catModalOpen: false,
      catEditItem: null,
      catForm: { ...INIT_CATEGORY_FORM },
      catErrors: {},

      // Sub Category Form Modal State
      subCatModalOpen: false,
      subCatEditItem: null,
      subCatForm: { ...INIT_SUBCATEGORY_FORM },
      subCatErrors: {},

      saving: false,
    };
  }

  // ─── Event Handlers ────────────────────────────────────────────────────────

  handleSubTabChange = (tab) => {
    this.setState({ subTab: tab });
  };

  // Category modal controls
  openCatModal = (item = null) => {
    if (item) {
      this.setState({
        catModalOpen: true,
        catEditItem: item,
        catForm: {
          video_category: item.video_category,
          estatus: item.estatus,
        },
        catErrors: {},
      });
    } else {
      this.setState({
        catModalOpen: true,
        catEditItem: null,
        catForm: { ...INIT_CATEGORY_FORM },
        catErrors: {},
      });
    }
  };

  closeCatModal = () => {
    this.setState({
      catModalOpen: false,
      catEditItem: null,
      catForm: { ...INIT_CATEGORY_FORM },
      catErrors: {},
    });
  };

  handleCatChange = (e) => {
    const { name, value } = e.target;
    this.setState(
      (prev) => ({
        catForm: {
          ...prev.catForm,
          [name]: name === "estatus" ? parseInt(value) : value,
        },
      }),
      () => {
        const errors = validateCategoryForm(this.state.catForm);
        this.setState({ catErrors: errors });
      }
    );
  };

  handleCatSubmit = async (e) => {
    e.preventDefault();
    const { catForm, catEditItem } = this.state;
    const { client, instituteId, onUpdate } = this.props;

    const errors = validateCategoryForm(catForm);
    if (Object.keys(errors).length > 0) {
      this.setState({ catErrors: errors });
      return;
    }

    this.setState({ saving: true });
    try {
      if (catEditItem) {
        // Edit Mode
        await client.mutate({
          mutation: UPDATE_LECTURE_CATEGORY,
          variables: {
            input: {
              id: catEditItem.id,
              video_category: catForm.video_category,
              institute_id: instituteId,
            },
          },
        });
      } else {
        // Add Mode
        await client.mutate({
          mutation: CREATE_LECTURE_CATEGORY,
          variables: {
            input: {
              video_category: catForm.video_category,
              institute_id: instituteId,
            },
          },
        });
      }
      this.closeCatModal();
      if (onUpdate) onUpdate(true);
    } catch (err) {
      console.error(err);
      alert("Failed to save category: " + err.message);
    } finally {
      this.setState({ saving: false });
    }
  };

  handleCatDelete = async (id, name) => {
    const { client, onUpdate } = this.props;
    if (!window.confirm(`Are you sure you want to delete Category "${name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await client.mutate({
        mutation: DELETE_LECTURE_CATEGORY,
        variables: { id },
      });
      alert("Category deleted successfully!");
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error(err);
      alert("Failed to delete category: " + err.message);
    }
  };

  // Sub Category modal controls
  openSubCatModal = (item = null) => {
    if (item) {
      this.setState({
        subCatModalOpen: true,
        subCatEditItem: item,
        subCatForm: {
          video_sub_category: item.video_sub_category,
          video_category_id: item.video_category_id,
          description: item.description || "",
          estatus: item.estatus,
        },
        subCatErrors: {},
      });
    } else {
      this.setState({
        subCatModalOpen: true,
        subCatEditItem: null,
        subCatForm: { ...INIT_SUBCATEGORY_FORM },
        subCatErrors: {},
      });
    }
  };

  closeSubCatModal = () => {
    this.setState({
      subCatModalOpen: false,
      subCatEditItem: null,
      subCatForm: { ...INIT_SUBCATEGORY_FORM },
      subCatErrors: {},
    });
  };

  handleSubCatChange = (e) => {
    const { name, value } = e.target;
    this.setState(
      (prev) => ({
        subCatForm: {
          ...prev.subCatForm,
          [name]: name === "estatus" || name === "video_category_id" ? parseInt(value) : value,
        },
      }),
      () => {
        const errors = validateSubCategoryForm(this.state.subCatForm);
        this.setState({ subCatErrors: errors });
      }
    );
  };

  handleSubCatSubmit = async (e) => {
    e.preventDefault();
    const { subCatForm, subCatEditItem } = this.state;
    const { client, onUpdate } = this.props;

    const errors = validateSubCategoryForm(subCatForm);
    if (Object.keys(errors).length > 0) {
      this.setState({ subCatErrors: errors });
      return;
    }

    this.setState({ saving: true });
    try {
      if (subCatEditItem) {
        // Edit Mode
        await client.mutate({
          mutation: UPDATE_LECTURE_SUBCATEGORY,
          variables: {
            input: {
              id: subCatEditItem.id,
              video_sub_category: subCatForm.video_sub_category,
              video_category_id: parseInt(subCatForm.video_category_id),
              description: subCatForm.description,
            },
          },
        });
      } else {
        // Add Mode
        await client.mutate({
          mutation: CREATE_LECTURE_SUBCATEGORY,
          variables: {
            input: {
              video_sub_category: subCatForm.video_sub_category,
              video_category_id: parseInt(subCatForm.video_category_id),
              description: subCatForm.description,
            },
          },
        });
      }
      this.closeSubCatModal();
      if (onUpdate) onUpdate(true);
    } catch (err) {
      console.error(err);
      alert("Failed to save subcategory: " + err.message);
    } finally {
      this.setState({ saving: false });
    }
  };

  handleSubCatDelete = async (id, name) => {
    const { client, onUpdate } = this.props;
    if (!window.confirm(`Are you sure you want to delete Sub Category "${name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await client.mutate({
        mutation: DELETE_LECTURE_SUBCATEGORY,
        variables: { id },
      });
      alert("Sub Category deleted successfully!");
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error(err);
      alert("Failed to delete subcategory: " + err.message);
    }
  };

  // ─── Table Columns Configurations ──────────────────────────────────────────

  getCategoryColumns = () => {
    return [
      {
        dataField: "id",
        text: "#",
        formatter: (cell, row, rowIndex) => rowIndex + 1,
        sort: true,
        headerStyle: { width: "100px", textAlign: "center" },
        style: { textAlign: "center" },
      },
      {
        dataField: "video_category",
        text: "Category Name",
        sort: true,
      },
      {
        dataField: "actions",
        text: "Actions",
        headerStyle: { width: "150px", textAlign: "center" },
        style: { textAlign: "center" },
        formatter: (cell, row) => (
          <div>
            <button
              onClick={() => this.openCatModal(row)}
              style={{ background: "none", border: "none", color: THEME.primary, cursor: "pointer" }}
              title="Edit Category"
            >
              <i className="fal fa-edit" />
            </button>
          </div>
        ),
      },
    ];
  };

  getSubCategoryColumns = () => {
    const { categories } = this.props;
    return [
      {
        dataField: "id",
        text: "#",
        formatter: (cell, row, rowIndex) => rowIndex + 1,
        sort: true,
        headerStyle: { width: "100px", textAlign: "center" },
        style: { textAlign: "center" },
      },
      {
        dataField: "video_sub_category",
        text: "Sub Category Name",
        sort: true,
      },
      {
        dataField: "video_category_id",
        text: "Parent Category",
        sort: true,
        formatter: (cell) => {
          const parent = categories.find((c) => Number(c.id) === Number(cell));
          return parent ? parent.video_category : "—";
        },
      },
      {
        dataField: "description",
        text: "Description",
        sort: true,
        formatter: (cell) => cell || "—",
      },
      {
        dataField: "actions",
        text: "Actions",
        headerStyle: { width: "150px", textAlign: "center" },
        style: { textAlign: "center" },
        formatter: (cell, row) => (
          <div>
            <button
              onClick={() => this.openSubCatModal(row)}
              style={{ background: "none", border: "none", color: THEME.primary, cursor: "pointer" }}
              title="Edit Sub Category"
            >
              <i className="fal fa-edit" />
            </button>
          </div>
        ),
      },
    ];
  };

  // ─── Rendering Helper Modules ──────────────────────────────────────────────

  renderModalHeader = (icon, title, onClose) => {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "14px",
          padding: "18px 22px",
          background: "linear-gradient(135deg, rgb(99, 102, 241), rgb(139, 92, 246))",
          color: "#fff",
        }}
      >
        <span
          style={{
            width: 40,
            height: 40,
            borderRadius: "11px",
            background: "rgba(255, 255, 255, 0.18)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "17px",
            flexShrink: 0,
          }}
        >
          <i className={icon} />
        </span>
        <span style={{ flex: 1, fontSize: "16px", fontWeight: 700, lineHeight: 1.25 }}>
          {title}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            background: "rgba(255, 255, 255, 0.18)",
            border: "none",
            color: "#fff",
            width: 32,
            height: 32,
            borderRadius: "9px",
            cursor: "pointer",
            fontSize: "15px",
            lineHeight: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <i className="fal fa-times" />
        </button>
      </div>
    );
  };

  render() {
    const { categories, subcategories } = this.props;
    const { subTab, catModalOpen, catForm, catErrors, subCatModalOpen, subCatForm, subCatErrors, saving } = this.state;

    const labelStyle = { fontSize: "12px", fontWeight: "600", color: THEME.textMuted, marginBottom: "6px", display: "block" };
    const inputStyle = { border: `1.5px solid ${THEME.border}`, borderRadius: "8px", padding: "10px 12px", fontSize: "14px", color: THEME.text };

    return (
      <div style={{ padding: "8px 0" }}>
        {/* Sub Navigation tabs matching the theme of ExpenseTypes */}
        <div style={{ background: "#f8fafc", borderBottom: "1.5px solid #e2e8f0", padding: "12px 0 0 0", marginBottom: "20px" }}>
          <ul className="nav nav-tabs" style={{ borderBottom: "none", gap: 8 }}>
            {[
              { key: "categories", label: "Lecture Categories", icon: "fal fa-folder-open" },
              { key: "subcategories", label: "Lecture Sub Categories", icon: "fal fa-folder-tree" },
            ].map(({ key, label, icon }) => {
              const active = subTab === key;
              return (
                <li className="nav-item" key={key}>
                  <a
                    className={`nav-link ${active ? "active" : ""}`}
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      this.handleSubTabChange(key);
                    }}
                    style={{
                      cursor: "pointer",
                      fontWeight: 700,
                      fontSize: 13,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "10px 18px",
                      color: active ? "#6366f1" : "#4b5563",
                      background: active ? "#fff" : "transparent",
                      borderColor: active ? "#e2e8f0 #e2e8f0 #fff" : "transparent",
                      borderWidth: "1.5px",
                      borderBottomWidth: active ? "2px" : "1.5px",
                      borderRadius: "8px 8px 0 0",
                      marginBottom: "-1.5px",
                      transition: "all 0.15s ease",
                    }}
                  >
                    <i className={icon} /> {label}
                  </a>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Action Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h6 style={{ margin: 0, fontWeight: "800", color: THEME.text, fontSize: "15px" }}>
            {subTab === "categories" ? "Manage Categories" : "Manage Sub Categories"}
          </h6>
          <Button
            onClick={() => (subTab === "categories" ? this.openCatModal() : this.openSubCatModal())}
            style={{
              background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
              border: "none",
              borderRadius: "8px",
              padding: "8px 16px",
              fontWeight: "700",
              fontSize: "13px",
            }}
          >
            <i className="fal fa-plus-circle mr-1" />
            {subTab === "categories" ? "Add Category" : "Add Sub Category"}
          </Button>
        </div>

        {/* Datatable Listings */}
        {subTab === "categories" ? (
          <CardLessDataTable
            parentData={categories}
            particlecolumns={this.getCategoryColumns()}
            name="lecture_categories"
            searchBar={true}
            placeholder="Search Lecture Categories..."
          />
        ) : (
          <CardLessDataTable
            parentData={subcategories}
            particlecolumns={this.getSubCategoryColumns()}
            name="lecture_subcategories"
            searchBar={true}
            placeholder="Search Lecture Sub Categories..."
          />
        )}

        {/* Category Modal Form */}
        <Modal show={catModalOpen} onHide={this.closeCatModal} centered>
          {this.renderModalHeader(
            "fal fa-folder-open",
            this.state.catEditItem ? "Edit Lecture Category" : "Add Lecture Category",
            this.closeCatModal
          )}
          <Form onSubmit={this.handleCatSubmit}>
            <Modal.Body style={{ padding: "20px" }}>
              <Form.Group className="mb-3">
                <Form.Label style={labelStyle}>Category Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="video_category"
                  placeholder="e.g. Core Subjects"
                  style={inputStyle}
                  value={catForm.video_category}
                  onChange={this.handleCatChange}
                  maxLength={45}
                  required
                />
                {catErrors.video_category && (
                  <p style={{ color: THEME.danger, fontSize: 11, marginTop: 4, margin: 0 }}>
                    ⛔ {catErrors.video_category}
                  </p>
                )}
              </Form.Group>
            </Modal.Body>
            <Modal.Footer style={{ background: THEME.bgLight, borderTop: `1px solid ${THEME.border}` }}>
              <Button variant="link" onClick={this.closeCatModal} style={{ color: THEME.textMuted, fontSize: "13px", fontWeight: "700" }}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                style={{
                  background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontWeight: "700",
                  padding: "8px 18px",
                }}
              >
                {saving ? "Saving..." : "Save Category"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Sub Category Modal Form */}
        <Modal show={subCatModalOpen} onHide={this.closeSubCatModal} centered>
          {this.renderModalHeader(
            "fal fa-folder-tree",
            this.state.subCatEditItem ? "Edit Lecture Sub Category" : "Add Lecture Sub Category",
            this.closeSubCatModal
          )}
          <Form onSubmit={this.handleSubCatSubmit}>
            <Modal.Body style={{ padding: "20px" }}>
              <Form.Group className="mb-3">
                <Form.Label style={labelStyle}>Parent Category *</Form.Label>
                <Form.Control
                  as="select"
                  name="video_category_id"
                  style={inputStyle}
                  value={subCatForm.video_category_id}
                  onChange={this.handleSubCatChange}
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.video_category}
                    </option>
                  ))}
                </Form.Control>
                {subCatErrors.video_category_id && (
                  <p style={{ color: THEME.danger, fontSize: 11, marginTop: 4, margin: 0 }}>
                    ⛔ {subCatErrors.video_category_id}
                  </p>
                )}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label style={labelStyle}>Sub Category Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="video_sub_category"
                  placeholder="e.g. Advanced Calculus"
                  style={inputStyle}
                  value={subCatForm.video_sub_category}
                  onChange={this.handleSubCatChange}
                  maxLength={45}
                  required
                />
                {subCatErrors.video_sub_category && (
                  <p style={{ color: THEME.danger, fontSize: 11, marginTop: 4, margin: 0 }}>
                    ⛔ {subCatErrors.video_sub_category}
                  </p>
                )}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label style={labelStyle}>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="description"
                  placeholder="Brief description about the topics covered"
                  style={inputStyle}
                  value={subCatForm.description}
                  onChange={this.handleSubCatChange}
                  maxLength={200}
                />
                <small className="d-flex justify-content-end text-muted mt-1" style={{ fontSize: "10px" }}>
                  {subCatForm.description.length}/200
                </small>
                {subCatErrors.description && (
                  <p style={{ color: THEME.danger, fontSize: 11, marginTop: 4, margin: 0 }}>
                    ⛔ {subCatErrors.description}
                  </p>
                )}
              </Form.Group>
            </Modal.Body>
            <Modal.Footer style={{ background: THEME.bgLight, borderTop: `1px solid ${THEME.border}` }}>
              <Button variant="link" onClick={this.closeSubCatModal} style={{ color: THEME.textMuted, fontSize: "13px", fontWeight: "700" }}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                style={{
                  background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontWeight: "700",
                  padding: "8px 18px",
                }}
              >
                {saving ? "Saving..." : "Save Sub Category"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </div>
    );
  }
}

export default LectureMasters;
